import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useRef} from "react";
import type {SkeletonBlockElement} from "slate";
import type {RenderElementProps} from "slate-react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {SchedulePolicy, useTimeout} from "@khanacademy/wonder-blocks-timing";
import {color, sizing, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {ShimmerRectangle} from "@khan/component-shimmer";
import {useCoeditorContext} from "@khan/guide-coeditor-core";

import {LoadingDots} from "../../../loading-dots.tsx";
import GuideCursor from "../../guide-cursor.tsx";

import lightbulbIcon from "@phosphor-icons/core/regular/lightbulb.svg";

export default function SkeletonBlockNode(
    props: RenderElementProps,
): React.ReactElement {
    const {attributes, element, children} = props;

    const {currentPlaceholderKey} = useCoeditorContext();

    const [showTip, setShowTip] = React.useState(false);
    const [almostThere, setAlmostThere] = React.useState(false);

    const ref = useRef<HTMLElement | null>(null);
    const tipIndexRef = useRef<number>(0);

    const isCurrent =
        (element as SkeletonBlockElement).placeholderKey ===
        currentPlaceholderKey;

    const {set: scheduleShowTip} = useTimeout(
        () => {
            setShowTip(true);
        },
        5000,
        {schedulePolicy: SchedulePolicy.OnDemand},
    );

    const {set: scheduleAlmostThere} = useTimeout(
        () => {
            setAlmostThere(true);
        },
        5000,
        {schedulePolicy: SchedulePolicy.OnDemand},
    );

    useEffect(() => {
        if (isCurrent) {
            tipIndexRef.current = Math.floor(Math.random() * TIPS().length);
            scheduleShowTip();
        }
    }, [isCurrent, scheduleShowTip]);

    useEffect(() => {
        if (isCurrent && showTip) {
            scheduleAlmostThere();
        }
    }, [isCurrent, showTip, scheduleAlmostThere]);

    useEffect(() => {
        if (!ref.current || !isCurrent) {
            // We only set up auto-scroll if this is the skeleton block that
            // is currently-being edited.
            return;
        }

        if (typeof window.IntersectionObserver === "undefined") {
            // Bail out if the browser/environment doesn't support this, which
            // may occur in tests (JSdom) or in very old browsers. We'll treat
            // auto-scroll as a nice-to-have.
            return;
        }

        const element = ref.current;

        // This sets up an observer to keep track of whether this skeleton
        // is currently visible (i.e. scrolled into view). If not, we'll get
        // false for `isIntersecting`, and we'll trigger a smooth-scroll to
        // keep it visible. Note that IntersectionObserver only runs its
        // callback when visibility _changes_ (goes from visible to not visible
        // or vice-versa).
        const io = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "nearest",
                    });
                }
            },
            {
                // This adds a margin to the bottom of the observed box around
                // the element, so that we'll autoscroll to try to keep this
                // margin visible below the element.
                rootMargin: "0px 0px 12px 0px",
            },
        );

        io.observe(element);

        return () => io.unobserve(element);
    }, [isCurrent]);

    return (
        <View {...attributes} style={styles.wrapper}>
            {isCurrent && (
                <View style={styles.cursorWrapper}>
                    <GuideCursor atEnd>{null}</GuideCursor>
                </View>
            )}
            {/* @ts-expect-error View props don't list contentEditable, but it works! */}
            <View contentEditable={false} ref={ref}>
                <ShimmerRectangle style={styles.shimmer} />
                <Strut size={spacing.xSmall_8} />
                <ShimmerRectangle style={styles.shimmer} />
                <Strut size={spacing.xSmall_8} />
                <ShimmerRectangle style={styles.shimmer} />
                {children}
                {isCurrent && showTip && (
                    <View style={styles.loading}>
                        <PhosphorIcon size="medium" icon={lightbulbIcon} />
                        <Strut size={spacing.xSmall_8} />
                        <Body>{TIPS()[tipIndexRef.current]}</Body>
                    </View>
                )}
                {isCurrent && showTip && almostThere && (
                    <View style={styles.loading}>
                        <Body style={styles.loadingDots}>
                            {t`Almost there`}
                            <Strut size={spacing.xxxSmall_4} />
                            <LoadingDots
                                color={color.offBlack32}
                                size="small"
                            />
                        </Body>
                    </View>
                )}
            </View>
        </View>
    );
}

function TIPS(): string[] {
    return [
        t`Try asking Khanmigo to break down a complex concept into smaller parts`,
        t`Ask Khanmigo to provide a simpler example to help students understand the concept`,
        t`Try asking Khanmigo to provide a more complex example to challenge students`,
        t`Ask Khanmigo to provide a real-world example to help students understand the concept`,
        t`Try asking Khanmigo to relate the topic to something students are familiar with`,
        t`Ask Khanmigo to include a reflection question`,
        t`Try asking Khanmigo to generate discussion questions for the class`,
        t`Ask Khanmigo to summarize the key points of the lesson`,
        t`Try asking Khanmigo to provide historical context for the lesson`,
    ];
}

const styles = StyleSheet.create({
    wrapper: {
        marginBlockEnd: spacing.medium_16,
        position: "relative",
    },
    shimmer: {
        borderRadius: sizing.size_080,
    },
    cursorWrapper: {
        position: "relative",
        insetInlineStart: spacing.xxxxSmall_2,
        top: spacing.medium_16,
    },
    loading: {
        flexDirection: "row",
        alignItems: "center",
        paddingBlockStart: spacing.small_12,
        color: color.offBlack32,
    },
    loadingDots: {
        display: "flex",
        flexDirection: "row",
    },
});
